import { format, subDays } from 'date-fns';
import type { Climb } from '../../types/climb';

/**
 * How much slack the indoor floor leaves around a climb's creation date, for
 * the gap between a wall being reset and somebody typing it in.
 */
const ENTRY_SLACK_DAYS = 7;

/**
 * The earliest day a send or an attempt on this climb may be dated, as
 * `yyyy-MM-dd`, or `undefined` for no floor at all.
 *
 * Indoors the climb did not exist before it was set: a send dated before that
 * is a typo, and the creation date (less a week of slack) is a useful guard.
 *
 * Outdoors that reasoning does not hold. A crag route was bolted long before
 * anyone entered it into DivePeaks, and the ascent being logged may be from
 * years earlier — the record's creation date says when the app learned about
 * the line, not when the line appeared. So there is no floor: any day in the
 * past is a day someone could have climbed it.
 *
 * The ceiling is always today, wherever the climb is; that one holds
 * everywhere, and each picker sets it for itself.
 */
export function logDateFloor(climb: Pick<Climb, 'created_at' | 'location'>): string | undefined {
  if (isOutdoor(climb)) return undefined;
  if (!climb.created_at) return undefined;

  return format(subDays(new Date(climb.created_at), ENTRY_SLACK_DAYS), 'yyyy-MM-dd');
}

/** Rock rather than plywood — the crag's own routes and boulders. */
function isOutdoor(climb: Pick<Climb, 'location'>): boolean {
  return climb.location?.type?.toLowerCase() === 'crag';
}
